/**
 * AGM Exempted AFS — 문서별 signers / Signers JSON.
 */
import { dedupePresetSigners } from '../../../presetSignerDedup';
import type { PresetDocCustSigner } from '../../../presetDocCustSave';
import type { AgmEntityPersonRow, AgmExemptedAfsFormModel } from './useFormPayload';
import type { AgmExemptedAfsDocKind } from './agmExemptedAfsDocVariableMap';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function toSignersJsonPayload(list: PresetDocCustSigner[]): { signers: string; Signers: string } | null {
  if (!list.length) return null;
  const json = JSON.stringify(list);
  return { signers: json, Signers: json };
}

function dedupKey(row: AgmEntityPersonRow): string {
  const cd = trim(row.contactCd);
  if (cd) return `cd:${cd.toUpperCase()}`;
  const reg = trim(row.contactRegNo || '').replace(/[\s-]/g, '').toUpperCase();
  if (reg) return `reg:${reg}`;
  return `name:${trim(row.name).toLowerCase()}`;
}

function appendRowIfNew(rows: AgmEntityPersonRow[], row: AgmEntityPersonRow): void {
  const name = trim(row.name);
  if (!name) return;
  const key = dedupKey({ ...row, name });
  if (rows.some((r) => dedupKey(r) === key)) return;
  rows.push({ ...row, name });
}

/** Members' Resolutions — 주주(멤버) 서명 대상 수집 */
export function collectAgmExemptedMemberSignerRows(m: AgmExemptedAfsFormModel): AgmEntityPersonRow[] {
  const rows: AgmEntityPersonRow[] = [];

  if (m.memberType === 'multiple') {
    for (const mem of m.members || []) {
      appendRowIfNew(rows, {
        name: trim(mem.name),
        contactCd: trim(mem.contactCd),
        role: 'shareholder',
      });
    }
  }

  if (m.memberType === 'multiple') {
    for (const mem of m.entityMembers || []) {
      appendRowIfNew(rows, { ...mem, role: 'shareholder' });
    }
  }

  return rows;
}

/** Sole Corporate — corporate member_name 1명만 (대표명·entityMembers 제외) */
function collectSoleCorporateMemberSignerRow(m: AgmExemptedAfsFormModel): AgmEntityPersonRow[] {
  const name = trim(m.memberName);
  if (!name) return [];
  return [{ name, role: 'shareholder' }];
}

/** Sole Individual — member_name 1명만 */
function collectSoleIndividualMemberSignerRow(m: AgmExemptedAfsFormModel): AgmEntityPersonRow[] {
  const name = trim(m.memberName);
  if (!name) return [];
  return [{ name, role: 'shareholder' }];
}

function buildMemberResolutionSignerJson(
  rows: AgmEntityPersonRow[],
): { signers: string; Signers: string } | null {
  const list = dedupePresetSigners(
    rows
      .map((r) => ({
        name: trim(r.name),
        email: trim(r.email || ''),
        role: 'shareholder' as const,
        contactCd: trim(r.contactCd),
        contactRegNo: trim(r.contactRegNo),
      }))
      .filter((s) => s.name),
    { defaultRole: 'shareholder' },
  );
  return toSignersJsonPayload(list);
}

/** Directors' Resolutions — 디렉터 전원 (3인 이상 수동 지정 시 form.signers) */
export function buildAgmExemptedAfsDirectorsResolutionSignerVariables(
  m: AgmExemptedAfsFormModel,
): { signers: string; Signers: string } | null {
  let rows: PresetDocCustSigner[];

  if (m.specifySigners && m.directorCount >= 3) {
    rows = (m.signers || [])
      .map((s) => ({ name: trim(s.name), email: '', role: 'director' as const }))
      .filter((s) => s.name);
  } else {
    rows = (m.entityDirectors || [])
      .map((d) => ({
        name: trim(d.name),
        email: trim(d.email || ''),
        role: 'director' as const,
        contactCd: trim(d.contactCd),
        contactRegNo: trim(d.contactRegNo),
      }))
      .filter((s) => s.name);
    if (!rows.length) {
      rows = (m.signers || [])
        .map((s) => ({ name: trim(s.name), email: '', role: 'director' as const }))
        .filter((s) => s.name);
    }
  }

  const deduped = dedupePresetSigners(rows);
  return toSignersJsonPayload(deduped);
}

/** Members' Resolutions (Multiple) — 주주 전원 */
export function buildAgmExemptedAfsMembersResolutionMultipleSignerVariables(
  m: AgmExemptedAfsFormModel,
): { signers: string; Signers: string } | null {
  return buildMemberResolutionSignerJson(collectAgmExemptedMemberSignerRows(m));
}

/** Members' Resolutions (Sole Individual) — member_name 1명 */
export function buildAgmExemptedAfsMembersResolutionSoleIndividualSignerVariables(
  m: AgmExemptedAfsFormModel,
): { signers: string; Signers: string } | null {
  return buildMemberResolutionSignerJson(collectSoleIndividualMemberSignerRow(m));
}

/** Members' Resolutions (Sole Corporate) — corporate member_name 1명만 */
export function buildAgmExemptedAfsMembersResolutionSoleCorporateSignerVariables(
  m: AgmExemptedAfsFormModel,
): { signers: string; Signers: string } | null {
  return buildMemberResolutionSignerJson(collectSoleCorporateMemberSignerRow(m));
}

export function buildAgmExemptedAfsMembersResolutionSignerVariables(
  m: AgmExemptedAfsFormModel,
  docKind: AgmExemptedAfsDocKind,
): { signers: string; Signers: string } | null {
  if (docKind === 'members_resolution_multiple') {
    return buildAgmExemptedAfsMembersResolutionMultipleSignerVariables(m);
  }
  if (docKind === 'members_resolution_sole_corporate') {
    return buildAgmExemptedAfsMembersResolutionSoleCorporateSignerVariables(m);
  }
  if (docKind === 'members_resolution_sole_individual') {
    return buildAgmExemptedAfsMembersResolutionSoleIndividualSignerVariables(m);
  }
  return null;
}
